import PropTypes from 'prop-types';
import React from 'react';
import { defineMessages, injectIntl, intlShape } from 'react-intl';
import Switch from 'brastrap/common/switch/Switch';
import MetaList from 'brastrap/common/meta-list/MetaList';
import Bag from 'brastrap/transactional/bag/Bag';
import Button from 'brastrap/common/button/Button';
import ButtonLink from 'brastrap/common/button-link/ButtonLink';

const STYLE_PREFIX = 'c-details-summary';

export const hasContent = items =>
  !!items.map(item => item.meta.length).find(length => length > 0);

const messages = defineMessages({
  preferred: {
    id: 'details-summary.preferred',
    defaultMessage: 'Preferred',
  },
});

const DetailsSummary = props => {
  let className = STYLE_PREFIX;

  if (props.preferred) {
    className += ` ${STYLE_PREFIX}--preferred`;
  }

  props.modifiers.forEach(modifier => {
    className += ` ${STYLE_PREFIX}--${modifier}`;
  });

  const displayActions = !!props.actions.length;
  const displayItems = !!props.items.length;
  const displayBag = props.bag && props.bag.items && props.bag.items.length;

  return (
    <section className={className}>
      {props.title && (
        <header
          className="c-details-summary__header"
          onClick={
            props.isCollapsable
              ? () => props.onClickToToggleContent()
              : undefined
          }
        >
          <h1 className="c-details-summary__title">
            {props.img && props.img.src && (
              <img
                className="c-details-summary__img"
                src={props.img.src}
                alt={props.img.alt}
                height={32}
              />
            )}
            {props.title}
          </h1>

          {props.isCollapsable && (
            <div className="c-details-summary__itemCountContainer">
              <p className="c-details-summary__itemCountContainerItem">
                Items:
              </p>
              <b className="c-details-summary__itemCountContainerItem">
                {props.itemCount}
              </b>
              <div
                className={`c-details-summary__chevron ${
                  props.showCollapsableContent
                    ? 'c-details-summary__rotateChevron'
                    : ''
                }`}
              />
            </div>
          )}

          {props.preferred && (
            <div className="c-details-summary__preferred">
              <Switch
                checked={props.preferred}
                group={props.intl.formatMessage(messages.preferred)}
                label="preferred"
              />
            </div>
          )}
        </header>
      )}

      {(!props.isCollapsable || props.showCollapsableContent) && (
        <div className="c-details-summary__main">
          {displayItems && (
            <div className="c-details-summary__items">
              {props.items.map((item, key) => (
                <div key={key} className="c-details-summary__item">
                  <MetaList {...item} preferred_id="preferred-address" />
                </div>
              ))}
            </div>
          )}

          {displayBag && <Bag isEditable={false} {...props.bag} />}

          {props.children}
        </div>
      )}

      {displayActions && (
        <footer className="c-details-summary__footer">
          <div className="c-details-summary__actions">
            {props.actions.map((action, index) => {
              if (action && action.label) {
                const Component = action.url ? ButtonLink : Button;
                return <Component key={index} {...action} />;
              }

              // Assume action is valid JSX
              return action;
            })}
          </div>
        </footer>
      )}
    </section>
  );
};

DetailsSummary.propTypes = {
  actions: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.object, PropTypes.element])
  ),
  bag: PropTypes.object,
  children: PropTypes.node,
  decisions: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.object, PropTypes.element])
  ),
  img: PropTypes.shape({
    alt: PropTypes.string.isRequired,
    src: PropTypes.string.isRequired,
  }),
  intl: intlShape,
  items: PropTypes.array,
  modifiers: PropTypes.array,
  preferred: PropTypes.bool,
  title: PropTypes.string,
  isCollapsable: PropTypes.bool,
  itemCount: PropTypes.number,
  showCollapsableContent: PropTypes.bool,
  onClickToToggleContent: PropTypes.func,
};

DetailsSummary.defaultProps = {
  actions: [],
  decisions: [],
  items: [],
  modifiers: [],
};

export default injectIntl(DetailsSummary);
